import React from 'react';
import {Controller, useFieldArray, useForm} from 'react-hook-form';
import {Button, Grid, TextField, Typography} from '@material-ui/core';

/* ---------- types ---------- */

export type FinancialSubmission = {
  id?: string;
  year: number;
  revenue: number;
  expenses: number;
  operatingCosts?: number;
  profit: number;
  financialYearEnd?: number;
  financialYearEndMonth?: number;
  notes?: string; // NEW
};

/* ---------- component ---------- */

export function FdFinancialHistoryForm(props: {
  defaultValues?: Partial<FinancialSubmission>[];
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (submissions: Partial<FinancialSubmission>[]) => void;
  cancelButtonTitle?: string;
  onCancel?: () => void;
  readonly?: boolean;
}) {
  const sortedDefaults = React.useMemo(() => {
    const defaults =
      props.defaultValues?.sort((a, b) => b.year! - a.year!) || [];
    return defaults;
  }, [props.defaultValues]);

  const {handleSubmit, control, reset} = useForm<{
    financials: Partial<FinancialSubmission>[];
  }>({
    defaultValues: {
      financials: sortedDefaults,
    },
  });

  // Force form reset when props change
  React.useEffect(() => {
    reset({
      financials: sortedDefaults,
    });
  }, [reset, sortedDefaults]);

  const {fields} = useFieldArray({
    control,
    name: 'financials',
  });

  const onSubmit = (data: {financials: Partial<FinancialSubmission>[]}) => {
    return props.onSubmit(data.financials);
  };

  return (
    <div className="generic-form-container financial-history-form">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          {fields.map((field, index) => (
            <Grid container spacing={2} className="form-section" key={field.id}>
              {/* YEAR (read-only) */}
              <Grid item xs={12}>
                <Controller
                  name={`financials.${index}.year`}
                  control={control}
                  render={({field: {value}}) => (
                    <Typography variant="subtitle2" gutterBottom>
                      {value}
                    </Typography>
                  )}
                />
              </Grid>

              {/* REVENUE */}
              <Grid item xs={6}>
                <Controller
                  name={`financials.${index}.revenue`}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <TextField
                      label="Revenue"
                      variant="outlined"
                      fullWidth
                      type="number"
                      value={value ?? ''}
                      onChange={(e) =>
                        onChange(parseFloat(e.target.value) || undefined)
                      }
                      error={!!error}
                      helperText={error?.message}
                      disabled={props.readonly}
                    />
                  )}
                  rules={{required: 'Revenue is required'}}
                />
              </Grid>

              {/* EXPENSES */}
              <Grid item xs={6}>
                <Controller
                  name={`financials.${index}.expenses`}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <TextField
                      label="Expenses"
                      variant="outlined"
                      fullWidth
                      type="number"
                      value={value ?? ''}
                      onChange={(e) =>
                        onChange(parseFloat(e.target.value) || undefined)
                      }
                      error={!!error}
                      helperText={error?.message}
                      disabled={props.readonly}
                    />
                  )}
                  rules={{required: 'Expenses are required'}}
                />
              </Grid>

              {/* OPERATING COSTS */}
              <Grid item xs={6}>
                <Controller
                  name={`financials.${index}.operatingCosts`}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <TextField
                      label="Operating Costs"
                      variant="outlined"
                      fullWidth
                      type="number"
                      value={value ?? ''}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        onChange(isNaN(val) ? undefined : val);
                      }}
                      error={!!error}
                      helperText={error?.message}
                      disabled={props.readonly}
                    />
                  )}
                />
              </Grid>

              {/* PROFIT */}
              <Grid item xs={6}>
                <Controller
                  name={`financials.${index}.profit`}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <TextField
                      label="Profit"
                      variant="outlined"
                      fullWidth
                      type="number"
                      value={value ?? ''}
                      onChange={(e) =>
                        onChange(parseFloat(e.target.value) || undefined)
                      }
                      error={!!error}
                      helperText={error?.message}
                      disabled={props.readonly}
                    />
                  )}
                  rules={{required: 'Profit is required'}}
                />
              </Grid>

              {/* NOTES (NEW) */}
              <Grid item xs={12}>
                <Controller
                  name={`financials.${index}.notes`}
                  control={control}
                  render={({field: {onChange, value}}) => (
                    <TextField
                      label="Notes"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={3}
                      value={value ?? ''}
                      onChange={onChange}
                      disabled={props.readonly}
                    />
                  )}
                />
              </Grid>
            </Grid>
          ))}

          {/* ACTION BUTTONS */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {props.cancelButtonTitle && (
                <Button
                  className="secondary-btn"
                  variant="contained"
                  fullWidth
                  onClick={props.onCancel}
                >
                  {props.cancelButtonTitle}
                </Button>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                className="primary-btn"
                type="submit"
                variant="contained"
                fullWidth
                color="primary"
                disabled={props.isLoading}
              >
                {props.submitButtonTitle}
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
}
